import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { take, throwError } from 'rxjs';

import { AuthorisationApiConfiguration } from '../../api/authorisation/authorisation-api-configuration';
import { GrantsService, UsersService } from '../../api/authorisation/services';
import { injectTranslate } from '../../functions/inject-functions/inject-translate';
import { UI_STRINGS } from '../../functions/language/uistrings-tolgee';
import { ToastLife, ToastType } from '../../modules/shared/ui-kit/toast/toast.component.types';
import { MessageService } from '../../services/message.service';
import { UserFacadeService } from '../../store/user/user-facade.service';

const whitelistedPaths = [UsersService.SetActiveStatePath];

export const readOnlyInterceptor: HttpInterceptorFn = (req, next) => {
  const apiConfig = inject(AuthorisationApiConfiguration);
  const messageService = inject(MessageService);
  const translate = injectTranslate();
  const userFacadeService = inject(UserFacadeService);

  const rootUrl = apiConfig.rootUrl || '';
  const { canManageBoardUsers, canManageTenantUsers } = userFacadeService.getUserPermissions();
  const isReadOnly = inject(UserFacadeService).isReadOnly();

  const whitelistedUrls = whitelistedPaths.map((url) => `${rootUrl}${url}`);

  const isGetRequest = req.method === 'GET';
  const isManagingBoardUsers =
    canManageBoardUsers &&
    (req.url.includes(rootUrl + GrantsService.GrantBoardPath.replace(/\/{[^}]+}/g, '')) ||
      req.url.includes(rootUrl + UsersService.CreateBoardUserPath));
  const isManagingTenantUsers =
    canManageTenantUsers &&
    (req.url.includes(rootUrl + GrantsService.GrantTenantPath.replace(/\/{[^}]+}/g, '')) ||
      req.url.includes(rootUrl + UsersService.CreateTenantUserPath));
  const isUrlWhitelisted = whitelistedUrls.includes(req.url);

  // Allow GET requests to pass through
  if (isGetRequest) {
    return next(req);
  }

  // Allow managing users if permitted
  if (isManagingBoardUsers || isManagingTenantUsers) {
    return next(req);
  }

  // Allow requests that are in the whitelist to pass through
  if (isUrlWhitelisted) {
    return next(req);
  }

  if (isReadOnly) {
    const toastTranslations$ = translate.toObservable({
      title: UI_STRINGS.READ_ONLY_INTERCEPTOR_TOAST.TITLE,
      text: UI_STRINGS.READ_ONLY_INTERCEPTOR_TOAST.TEXT,
    });

    // Show a toast notification to inform the user of the read-only state
    toastTranslations$.pipe(take(1)).subscribe((translations) => {
      messageService.openToast({
        type: ToastType.ERROR,
        toastLife: ToastLife.LONG,
        ...translations,
      });
    });

    console.warn(`Blocked ${req.method} request to ${req.url} due to read-only mode.`);
    // Return an error observable to signify the blocked request
    return throwError(() => new Error(`Blocked ${req.method} request to ${req.url} due to read-only mode.`));
  }

  // If not in read-only mode, allow the request
  return next(req);
};
